/**
 * SuperAdminPortal Component
 * 
 * Admin dashboard for managing all stores, viewing statistics, and performing admin operations.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Object} props.currentUser - Current super admin user
 * @param {Function} props.onLogout - Callback to logout
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Store, Users, Crown, LogOut, Edit, Eye, Plus, TrendingUp, Mail, Trash2, UserCog, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';
import { formatMonDD } from '../../utils/formatters';

function SuperAdminPortal({ currentUser, onLogout, onImpersonate, onViewAuditLogs }) {
  const [stores, setStores] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateStore, setShowCreateStore] = useState(false);
  const [showEditStore, setShowEditStore] = useState(false);
  const [editingStore, setEditingStore] = useState(null);
  const [selectedStore, setSelectedStore] = useState(null);
  
  // New store form
  const [newStore, setNewStore] = useState({
    name: '',
    slug: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    phone: '',
    primaryContactName: '',
    primaryContactEmail: '',
    primaryContactPhone: '',
    planType: 'free',
    trialEndsAt: ''
  });

  useEffect(() => {
    loadStores();
  }, []);

  const loadStores = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/stores`, {
        headers: {
          'X-User-Role': 'super_admin',
        },
      });
      setStores(response.data);
    } catch (error) {
      console.error('Failed to load stores:', error);
      toast.error(error.response?.data?.detail || 'Failed to load stores');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStore = async () => {
    try {
      if (!newStore.name || !newStore.phone) {
        toast.error('Store name and phone are required');
        return;
      }

      if (!newStore.primaryContactEmail) {
        toast.error('Owner email is required');
        return;
      }

      if (!newStore.primaryContactName) {
        toast.error('Owner name is required');
        return;
      }

      setLoading(true);
      
      // Auto-generate slug if not provided
      const slug = newStore.slug || newStore.name.toLowerCase().replace(/\s+/g, '-');
      
      const payload = {
        store: {
          ...newStore,
          slug,
          trialEndsAt: newStore.trialEndsAt || new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString()
        },
        owner_email: newStore.primaryContactEmail,
        owner_name: newStore.primaryContactName,
        frontend_url: window.location.origin  // Pass current frontend URL for email links
      };

      const response = await axios.post(`${API}/stores/create-with-owner`, payload, {
        headers: {
          'X-User-Role': 'super_admin',
        },
      });
      
      if (response.data.email_sent) {
        toast.success('Store created! Welcome email with credentials sent to owner.');
      } else {
        toast.error('Store created but email failed to send. Check AWS SES - recipient email must be verified in Sandbox mode.');
        console.error('Email sending failed. AWS SES details:', response.data.note);
      }
      
      setShowCreateStore(false);
      setNewStore({
        name: '',
        slug: '',
        address: '',
        city: '',
        state: '',
        zip: '',
        phone: '',
        primaryContactName: '',
        primaryContactEmail: '',
        primaryContactPhone: '',
        planType: 'free',
        trialEndsAt: ''
      });
      loadStores();
    } catch (error) {
      console.error('Failed to create store:', error);
      toast.error(error.response?.data?.detail || 'Failed to create store');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSubscription = async (storeId, planType) => {
    try {
      setLoading(true);
      await axios.put(`${API}/stores/${storeId}/subscription?plan_type=${planType}`, null, {
        headers: {
          'X-User-Role': 'super_admin',
        },
      });
      toast.success(`Subscription updated to ${planType}`);
      loadStores();
    } catch (error) {
      console.error('Failed to update subscription:', error);
      toast.error('Failed to update subscription');
    } finally {
      setLoading(false);
    }
  };
  const handleDeleteStore = async (storeId) => {
    if (!window.confirm('Are you sure you want to permanently delete this store and ALL its data? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await axios.delete(`${API}/stores/${storeId}`, {
        headers: {
          'X-User-Role': 'super_admin',
        },
      });
      toast.success('Store deleted successfully');
      loadStores();
    } catch (error) {
      console.error('Failed to delete store:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete store');
    } finally {
      setLoading(false);
    }
  };

  const handleEditStore = async (store) => {
    try {
      // Fetch all users for this store
      const token = localStorage.getItem('authToken');
      const usersResponse = await axios.get(`${API}/auth/users`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'X-User-Role': 'super_admin',
          'X-User-Email': currentUser?.email
        }
      });
      
      // Find the store owner
      const owner = usersResponse.data.find(u => 
        u.storeId === store.storeId && u.role === 'store_admin'
      );
      
      console.log('Found owner:', owner);
      
      setEditingStore({
        ...store,
        ownerName: owner?.name || '',
        ownerEmail: owner?.email || ''
      });
    } catch (error) {
      console.error('Failed to fetch owner info:', error);
      // Still allow editing store info
      setEditingStore({
        ...store,
        ownerName: '',
        ownerEmail: ''
      });
    }
    setShowEditStore(true);
  };

  const handleUpdateStore = async () => {
    try {
      if (!editingStore.name || !editingStore.phone) {
        toast.error('Store name and phone are required');
        return;
      }

      setLoading(true);
      
      const updateData = {
        name: editingStore.name,
        address: editingStore.address,
        city: editingStore.city,
        state: editingStore.state,
        zip: editingStore.zip,
        phone: editingStore.phone,
        primaryContactName: editingStore.primaryContactName,
        primaryContactEmail: editingStore.primaryContactEmail,
        primaryContactPhone: editingStore.primaryContactPhone
      };

      // Update store settings
      await axios.put(`${API}/stores/${editingStore.storeId}/settings`, updateData, {
        headers: {
          'X-User-Role': 'super_admin',
        },
      });
      
      // If primaryContactEmail or primaryContactName changed, update the owner user as well
      if (editingStore.primaryContactName || editingStore.primaryContactEmail) {
        try {
          // Find the owner user for this store
          const usersResponse = await axios.get(`${API}/auth/users`, {
            headers: {
              'X-User-Role': 'super_admin',
            },
          });
          
          const storeOwner = usersResponse.data.find(
            user => user.storeId === editingStore.storeId && user.role === 'store_admin'
          );
          
          if (storeOwner) {
            // Update owner user information
            const userUpdateData = {
              name: editingStore.primaryContactName || storeOwner.name,
              email: editingStore.primaryContactEmail || storeOwner.email,
              phone: editingStore.primaryContactPhone || storeOwner.phone,
              role: storeOwner.role
            };
            
            await axios.put(`${API}/auth/users/${storeOwner.id}`, userUpdateData, {
              headers: {
                'X-User-Role': 'super_admin',
              },
            });
          }
        } catch (userError) {
          console.error('Failed to update owner user:', userError);
          // Don't fail the whole operation if user update fails
        }
      }
      
      toast.success('Store and owner information updated successfully');
      setShowEditStore(false);
      setEditingStore(null);
      loadStores();
    } catch (error) {
      console.error('Failed to update store:', error);
      toast.error(error.response?.data?.detail || 'Failed to update store');
    } finally {
      setLoading(false);
    }
  };


  const handleResendCredentials = async (storeId, storeName) => {
    if (!window.confirm(`Generate new temporary password and send to ${storeName} owner's email?\n\nThis will reset their current password.`)) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API}/stores/${storeId}/resend-credentials`, {
        frontend_url: window.location.origin  // Pass current frontend URL for email links
      }, {
        headers: {
          'X-User-Role': 'super_admin',
        },
      });
      
      if (response.data.email_sent) {
        toast.success(`New credentials sent to ${response.data.owner_email}`);
      } else {
        toast.error(`Password reset but email failed. Recipient email must be verified in AWS SES Sandbox mode.`);
      }
    } catch (error) {
      console.error('Failed to resend credentials:', error);
      toast.error(error.response?.data?.detail || 'Failed to resend credentials');
    } finally {
      setLoading(false);
    }
  };




  const getPlanBadgeColor = (plan) => {
    switch (plan) {
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'base': return 'bg-blue-100 text-blue-800';
      case 'free': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Super Admin Portal</h1>
              <p className="text-sm text-gray-600">Welcome, {currentUser?.name}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              {onImpersonate && (
                <Button onClick={onImpersonate} variant="outline" data-testid="open-impersonate-btn">
                  <UserCog className="w-4 h-4 mr-2" />
                  Impersonate
                </Button>
              )}
              {onViewAuditLogs && (
                <Button onClick={onViewAuditLogs} variant="outline" data-testid="open-audit-logs-btn">
                  <FileText className="w-4 h-4 mr-2" />
                  Audit Trail
                </Button>
              )}
              <Button onClick={onLogout} variant="outline">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Stores</p>
                  <p className="text-2xl font-bold text-gray-900">{stores.length}</p>
                </div>
                <Store className="w-8 h-8 text-orange-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Premium Stores</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {stores.filter(s => s.planType === 'premium').length}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Base Stores</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {stores.filter(s => s.planType === 'base').length}
                  </p>
                </div>
                <TrendingUp className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Free/Trial</p>
                  <p className="text-2xl font-bold text-gray-600">
                    {stores.filter(s => s.planType === 'free').length}
                  </p>
                </div>
                <Users className="w-8 h-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Create Store Button */}
        <div className="mb-6">
          <Button 
            onClick={() => setShowCreateStore(!showCreateStore)}
            className="bg-orange-600 hover:bg-orange-700"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Store
          </Button>
        </div>

        {/* Create Store Form */}
        {showCreateStore && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Create New Store</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Store Name *</Label>
                  <Input
                    value={newStore.name}
                    onChange={(e) => setNewStore({...newStore, name: e.target.value})}
                    placeholder="e.g., Bawarchi Dallas"
                  />
                </div>
                <div>
                  <Label>Slug (URL)</Label>
                  <Input
                    value={newStore.slug}
                    onChange={(e) => setNewStore({...newStore, slug: e.target.value})}
                    placeholder="e.g., bawarchi-dallas"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={newStore.phone}
                    onChange={(e) => setNewStore({...newStore, phone: e.target.value})}
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={newStore.address}
                    onChange={(e) => setNewStore({...newStore, address: e.target.value})}
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={newStore.city}
                    onChange={(e) => setNewStore({...newStore, city: e.target.value})}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={newStore.state}
                    onChange={(e) => setNewStore({...newStore, state: e.target.value})}
                    placeholder="TX"
                  />
                </div>
                <div>
                  <Label>Owner Name *</Label>
                  <Input
                    value={newStore.primaryContactName}
                    onChange={(e) => setNewStore({...newStore, primaryContactName: e.target.value})}
                    placeholder="Owner full name"
                  />
                  <p className="text-xs text-gray-500 mt-1">This person will receive login credentials via email</p>
                </div>
                <div>
                  <Label>Owner Email *</Label>
                  <Input
                    type="email"
                    value={newStore.primaryContactEmail}
                    onChange={(e) => setNewStore({...newStore, primaryContactEmail: e.target.value})}
                    placeholder="owner@example.com"
                  />
                  <p className="text-xs text-gray-500 mt-1">Auto-generated password will be sent to this email</p>
                </div>
                <div>
                  <Label>Subscription Plan</Label>
                  <select
                    value={newStore.planType}
                    onChange={(e) => setNewStore({...newStore, planType: e.target.value})}
                    className="w-full h-9 px-3 border border-gray-300 rounded-md"
                  >
                    <option value="free">Free (14-day trial)</option>
                    <option value="base">Base ($14.99/mo)</option>
                    <option value="premium">Premium ($24.99/mo)</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleCreateStore} disabled={loading}>
                  Create Store
                </Button>
                <Button onClick={() => setShowCreateStore(false)} variant="outline">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Edit Store Modal */}
        {showEditStore && editingStore && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Edit Store: {editingStore.name}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label>Store Name *</Label>
                  <Input
                    value={editingStore.name}
                    onChange={(e) => setEditingStore({...editingStore, name: e.target.value})}
                    placeholder="e.g., Bawarchi Dallas"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={editingStore.phone}
                    onChange={(e) => setEditingStore({...editingStore, phone: e.target.value})}
                    placeholder="(123) 456-7890"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={editingStore.address}
                    onChange={(e) => setEditingStore({...editingStore, address: e.target.value})}
                    placeholder="Street address"
                  />
                </div>
                <div>
                  <Label>City</Label>
                  <Input
                    value={editingStore.city}
                    onChange={(e) => setEditingStore({...editingStore, city: e.target.value})}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label>State</Label>
                  <Input
                    value={editingStore.state}
                    onChange={(e) => setEditingStore({...editingStore, state: e.target.value})}
                    placeholder="TX"
                  />
                </div>
                <div>
                  <Label>ZIP Code</Label>
                  <Input
                    value={editingStore.zip}
                    onChange={(e) => setEditingStore({...editingStore, zip: e.target.value})}
                    placeholder="75001"
                  />
                </div>
                <div>
                  <Label>Owner Name *</Label>
                  <Input
                    value={editingStore.ownerName || ''}
                    onChange={(e) => setEditingStore({...editingStore, ownerName: e.target.value})}
                    placeholder="Store owner name"
                  />
                </div>
                <div>
                  <Label>Owner Email *</Label>
                  <Input
                    type="email"
                    value={editingStore.ownerEmail || ''}
                    onChange={(e) => setEditingStore({...editingStore, ownerEmail: e.target.value})}
                    placeholder="owner@example.com"
                  />
                </div>
              </div>
              <div className="flex gap-2 mt-4">
                <Button onClick={handleUpdateStore} disabled={loading}>
                  {loading ? 'Updating...' : 'Update Store'}
                </Button>
                <Button onClick={() => { setShowEditStore(false); setEditingStore(null); }} variant="outline">
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        )}


        {/* Stores Table */}
        <Card>
          <CardHeader>
            <CardTitle>All Stores</CardTitle>
          </CardHeader>
          <CardContent>
            {loading && stores.length === 0 ? (
              <p className="text-center text-gray-500 py-8">Loading stores...</p>
            ) : stores.length === 0 ? (
              <p className="text-center text-gray-500 py-8">No stores yet. Create your first store!</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b">
                      <th className="text-left py-3 px-4">Store Name</th>
                      <th className="text-left py-3 px-4">Location</th>
                      <th className="text-left py-3 px-4">Phone</th>
                      <th className="text-left py-3 px-4">Signup Date</th>
                      <th className="text-left py-3 px-4">Expiry Date</th>
                      <th className="text-left py-3 px-4">Plan</th>
                      <th className="text-left py-3 px-4">Status</th>
                      <th className="text-left py-3 px-4">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stores.map(store => {
                      // Format dates
                      const formatDate = (dateStr) => {
                        if (!dateStr) return '-';
                        try {
                          const date = new Date(dateStr);
                          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                        } catch {
                          return '-';
                        }
                      };
                      
                      return (
                      <tr key={store.storeId} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4 font-medium">{store.name}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {store.city}, {store.state}
                        </td>
                        <td className="py-3 px-4 text-sm">{store.phone}</td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {formatDate(store.created_at)}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600">
                          {store.trial_end_date ? (
                            <span title="Trial ends">{formatDate(store.trial_end_date)}</span>
                          ) : store.next_billing_date ? (
                            <span title="Next billing date">{formatDate(store.next_billing_date)}</span>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPlanBadgeColor(store.planType)}`}>
                            {store.planType.toUpperCase()}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            store.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {store.status}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex gap-2 flex-wrap">
                            <select
                              value={store.planType}
                              onChange={(e) => handleUpdateSubscription(store.storeId, e.target.value)}
                              className="text-sm border border-gray-300 rounded px-2 py-1"
                              disabled={loading}
                            >
                              <option value="free">Free</option>
                              <option value="base">Base</option>
                              <option value="premium">Premium</option>
                            </select>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                              disabled={loading}
                              onClick={() => handleEditStore(store)}
                              title="Edit store details"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 border-green-200 hover:bg-green-50"
                              disabled={loading}
                              onClick={() => handleResendCredentials(store.storeId, store.name)}
                              title="Resend owner credentials"
                            >
                              <Mail className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                              disabled={loading}
                              onClick={() => handleDeleteStore(store.storeId)}
                              title="Delete store"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
} // End of App component


export default SuperAdminPortal;
